/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import websocket from 'services/websocket.connections';
import {fetchAllUsageStatistics} from 'services/analytics.service';
import {IUsageStatistics} from 'interfaces';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

/**
 * ACTION TYPES
 */

export const USAGE_STATISTICS_FETCHING = 'usage-statistics::USAGE_STATISTICS_FETCHING' as const;
export const USAGE_STATISTICS_FETCHING_DONE = 'usage-statistics::USAGE_STATISTICS_FETCHING_DONE' as const;
export const USAGE_STATISTICS_FETCHING_ERROR = 'usage-statistics::USAGE_STATISTICS_FETCHING_ERROR' as const;

/**
 * INTERFACES
 */

interface IRequestUsageStatistics {
  type: typeof USAGE_STATISTICS_FETCHING;
}
interface IReceiveUsageStatistics {
  type: typeof USAGE_STATISTICS_FETCHING_DONE;
  payload: {data: IUsageStatistics};
}
interface IFailedUsageStatistics {
  type: typeof USAGE_STATISTICS_FETCHING_ERROR;
  error: null | IPortalError;
}

export type UsageStatisticsActionType = IRequestUsageStatistics | IReceiveUsageStatistics | IFailedUsageStatistics;

interface IUsageStatisticsActions {
  request: () => UsageStatisticsActionType;
  receive: (payload) => UsageStatisticsActionType;
  failed: (error: null | IPortalError) => UsageStatisticsActionType;
}

/**
 * ACTIONS
 */

const usageStatisticsActions: IUsageStatisticsActions = {
  request: () => ({type: USAGE_STATISTICS_FETCHING}),
  receive: payload => ({
    type: USAGE_STATISTICS_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: USAGE_STATISTICS_FETCHING_ERROR,
    error
  })
};

export const getUsageStatistics = (start: number, end: number, applicationId: string) => async(dispatch: Dispatch<UsageStatisticsActionType>): Promise<IUsageStatistics> => {
  const {
    request,
    receive,
    failed
  } = usageStatisticsActions;

  dispatch(request());

  try {
    const socket = websocket.getActiveSocket();
    const usageStatistics = await fetchAllUsageStatistics(socket, start, end, applicationId);

    dispatch(receive({data: usageStatistics}));

    return usageStatistics;
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};